import { useState, useRef } from "react";
import { Upload, X, Check, Loader2, Image as ImageIcon } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable image upload component.
 * Props:
 *   currentUrl - current image URL (for preview)
 *   onUpload - callback(newUrl) when upload succeeds
 *   password - admin password for auth
 *   label - optional label text
 *   aspectClass - optional aspect ratio class (e.g. "aspect-video", "aspect-[4/5]")
 */
export default function ImageUploader({ currentUrl, onUpload, password, label, aspectClass = "aspect-video" }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("File too large (max 10MB)");
      return;
    }

    setError("");
    setPreview(URL.createObjectURL(file));
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await axios.post(`${API}/images/upload`, formData, {
        headers: {
          "x-admin-password": password,
          "Content-Type": "multipart/form-data",
        },
      });

      // Return full URL for the image
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      onUpload(imageUrl);
      setPreview(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Upload failed");
      setPreview(null);
    }
    setUploading(false);
  };

  const displayUrl = preview || currentUrl;

  return (
    <div className="space-y-2">
      {label && (
        <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
          {label}
        </label>
      )}
      <div
        className={`relative ${aspectClass} overflow-hidden rounded-lg border-2 border-dashed border-white/10 hover:border-[#E63946]/30 transition-colors bg-[#0A0A0C] cursor-pointer group`}
        onClick={() => fileRef.current?.click()}
        data-testid="image-uploader"
      >
        {displayUrl ? (
          <>
            <img
              src={displayUrl}
              alt="Upload preview"
              className="w-full h-full object-cover"
            />
            {/* Overlay on hover */}
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <div className="text-center">
                <Upload size={20} className="text-white mx-auto mb-1" />
                <span className="text-xs text-zinc-300">Replace Image</span>
              </div>
            </div>
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
            <ImageIcon size={24} className="text-zinc-600" />
            <span className="text-xs text-zinc-500">Click to upload</span>
            <span className="text-[10px] text-zinc-700">JPEG, PNG, WebP — Max 10MB</span>
          </div>
        )}

        {/* Loading overlay */}
        {uploading && (
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
            <Loader2 size={24} className="text-[#E63946] animate-spin" />
          </div>
        )}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFile}
        className="hidden"
      />

      {error && <p className="text-xs text-red-400">{error}</p>}

      {/* URL input for manual paste */}
      <div className="flex gap-2">
        <input
          type="text"
          value={currentUrl || ""}
          onChange={(e) => onUpload(e.target.value)}
          placeholder="Or paste image URL..."
          className="flex-1 px-3 py-1.5 text-xs bg-[#050505] border border-white/10 text-white rounded placeholder:text-zinc-600 focus:border-[#E63946] focus:outline-none"
        />
      </div>
    </div>
  );
}
